// Client mirror of functions-common/src/repeat/progress.ts. Keep in sync.

import { formatZonedDateKey, periodKeyFor, resolveWorkspaceTimeZone } from './keys'
import { isDoneFor, isRepeatingItem } from './completions'
import { periodsInWindow } from './window'
import type {
  RepeatItemInput,
  RepeatPagesInput,
} from './types'

export interface ItemPeriodProgressResult {
  done: number
  due: number
  ratio: number
}

export interface RollupProgressItem {
  weightage?: unknown
  ratio?: number | null
  board?: string | null
  deletedAt?: unknown
}

export interface RollupOverride {
  ratio?: number | null
  isCompleted?: boolean | null
  workflowStatus?: string | null
  periodProgress?: { ratio?: number | null } | null
  checklistType?: string | null
}

/**
 * Returns true if a page completion entry represents a completed/done state:
 * isCompleted true, or workflowStatus COMPLETED or NOT_REQUIRED.
 * Matches isDoneFor (completions.ts:48).
 */
export function isCompletionEntryDone(entry: unknown): boolean {
  if (!entry || typeof entry !== 'object') return false
  const e = entry as { isCompleted?: unknown; workflowStatus?: unknown }
  return (
    Boolean(e.isCompleted) ||
    e.workflowStatus === 'COMPLETED' ||
    e.workflowStatus === 'NOT_REQUIRED'
  )
}

/**
 * Maps a checklist item (or document data or node) to a RollupProgressItem.
 * Repeating -> periodProgress.ratio (missing -> 0).
 * One-off -> 1 when isCompleted, workflowStatus COMPLETED or NOT_REQUIRED, else 0.
 * override allows callers (e.g. delta processor) to pass a new target state.
 */
export function rollupInputFor(
  item: unknown,
  override?: RollupOverride | null,
): RollupProgressItem {
  if (!item || typeof item !== 'object') {
    return { weightage: 1, ratio: 0 }
  }
  const raw = item as Record<string, unknown>
  const data = (raw.data && typeof raw.data === 'object')
    ? (raw.data as Record<string, unknown>)
    : raw

  const checklistType = override?.checklistType ?? data.checklistType
  const isRepeat = checklistType === 'DAILY' || checklistType === 'WEEKLY'

  let ratio: number
  if (isRepeat) {
    if (typeof override?.ratio === 'number' && Number.isFinite(override.ratio)) {
      ratio = override.ratio
    } else if (
      override?.periodProgress &&
      typeof override.periodProgress.ratio === 'number' &&
      Number.isFinite(override.periodProgress.ratio)
    ) {
      ratio = override.periodProgress.ratio
    } else {
      const storedProgress = data.periodProgress as { ratio?: unknown } | undefined
      ratio = typeof storedProgress?.ratio === 'number' && Number.isFinite(storedProgress.ratio)
        ? storedProgress.ratio
        : 0
    }
  } else {
    if (typeof override?.ratio === 'number' && Number.isFinite(override.ratio)) {
      ratio = override.ratio
    } else {
      const isCompleted = override?.isCompleted !== undefined
        ? Boolean(override.isCompleted)
        : Boolean(data.isCompleted)
      const workflowStatus = override?.workflowStatus !== undefined
        ? override.workflowStatus
        : data.workflowStatus
      const isDone = isCompleted || workflowStatus === 'COMPLETED' || workflowStatus === 'NOT_REQUIRED'
      ratio = isDone ? 1 : 0
    }
  }

  return {
    weightage: data.weightage,
    ratio,
    board: (data.board as string | null | undefined) ?? null,
    deletedAt: data.deletedAt,
  }
}


/**
 * Normalizes weight for progress calculation:
 * A finite number > 0 is used as is; anything else (<=0, undefined, NaN, null) defaults to 1.
 * Parity with cascadeEngine.ts normalizeWeight.
 */
export function normalizeProgressWeight(weightage: unknown): number {
  if (typeof weightage !== 'number' || !Number.isFinite(weightage) || weightage <= 0) {
    return 1
  }
  return weightage
}

/**
 * Computes progress for an individual or repeating checklist item.
 * One-off items return done/due 1/1 (if done) or 0/1 (if not done).
 * Repeating items return done periods / due periods so far within their recurringWindow.
 * The current period (today for DAILY, this ISO week for WEEKLY) is due only once it is done or over.
 */
export function itemPeriodProgress(
  item: RepeatItemInput | null | undefined,
  pages?: RepeatPagesInput | null,
  timeZone?: string,
  now: Date | number | string = new Date(),
): ItemPeriodProgressResult {
  if (!item) {
    return { done: 0, due: 0, ratio: 0 }
  }

  const isRepeat = isRepeatingItem(item)
  if (!isRepeat) {
    const done = isDoneFor(item, '', pages)
    return {
      done: done ? 1 : 0,
      due: 1,
      ratio: done ? 1 : 0,
    }
  }

  // Repeating item (DAILY or WEEKLY)
  if (item.repeatPaused === true || item.recurringWindow?.repeatPaused === true) {
    return { done: 0, due: 0, ratio: 0 }
  }

  const window = item.recurringWindow
  const startRaw = window?.startDate
  const endRaw = window?.endDate
  if (!startRaw || !endRaw) {
    return { done: 0, due: 0, ratio: 0 }
  }

  const startIso = startRaw.slice(0, 10)
  const endIso = endRaw.slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startIso) || !/^\d{4}-\d{2}-\d{2}$/.test(endIso)) {
    return { done: 0, due: 0, ratio: 0 }
  }
  if (startIso > endIso) {
    return { done: 0, due: 0, ratio: 0 }
  }

  const tz = resolveWorkspaceTimeZone(timeZone)
  const nowDate = now instanceof Date ? now : new Date(now)
  const todayIso = formatZonedDateKey(nowDate, tz)

  // Before window opens: due 0 -> ratio 0
  if (todayIso < startIso) {
    return { done: 0, due: 0, ratio: 0 }
  }

  const cadence = item.checklistType === 'WEEKLY' ? 'WEEKLY' : 'DAILY'
  const periods = periodsInWindow(cadence, window, tz, nowDate)
  if (periods.length === 0) {
    return { done: 0, due: 0, ratio: 0 }
  }

  const currentPeriod = periodKeyFor(cadence, nowDate, tz)
  const isWindowEnded = Boolean(endIso && todayIso > endIso)

  let doneCount = 0
  let dueCount = 0

  for (const periodKey of periods) {
    const done = isDoneFor(item, periodKey, pages)
    if (done) {
      doneCount++
    }

    // Is this period due?
    // Past periods are due whether done or not.
    // If the window has ended, all periods in the window are past/over -> due.
    // The current period is due only once it is done or over.
    const isPast = periodKey < currentPeriod
    if (isPast || isWindowEnded) {
      dueCount++
    } else if (periodKey === currentPeriod) {
      if (done) {
        dueCount++
      }
    }
  }

  const ratio = dueCount > 0 ? doneCount / dueCount : 0

  return {
    done: doneCount,
    due: dueCount,
    ratio: Math.max(0, Math.min(1, ratio)),
  }
}

/**
 * Rolls up items into parent progress:
 * round(Σ weight × ratio ÷ Σ weight × 100), clamped 0–100.
 * Excludes items with board === 'BIN' or deletedAt.
 */
export function rollupProgress(items: readonly RollupProgressItem[] | null | undefined): number {
  if (!items || items.length === 0) return 0

  const activeItems = items.filter((item) => item?.board !== 'BIN' && !item?.deletedAt)
  if (activeItems.length === 0) return 0

  let totalWeight = 0
  let weightedProgress = 0

  for (const item of activeItems) {
    const weight = normalizeProgressWeight(item.weightage)
    const rawRatio = item.ratio
    const ratio = typeof rawRatio === 'number' && Number.isFinite(rawRatio) ? rawRatio : 0
    const clampedRatio = Math.max(0, Math.min(1, ratio))
    totalWeight += weight
    weightedProgress += weight * clampedRatio
  }

  if (totalWeight <= 0) return 0

  return Math.max(0, Math.min(100, Math.round((weightedProgress / totalWeight) * 100)))
}

export const SHARED_PROGRESS_ROLLUP_CASES = [
  {
    name: 'one-off completed with repeating sibling 100% done',
    items: [
      { id: '1', checklistType: 'INDIVIDUAL', isCompleted: true, weightage: 1 },
      { id: '2', checklistType: 'DAILY', periodProgress: { done: 3, due: 3, ratio: 1.0 }, weightage: 1 },
    ],
    expected: 100,
  },
  {
    name: 'one-off completed with repeating sibling 50% done',
    items: [
      { id: '1', checklistType: 'INDIVIDUAL', isCompleted: true, weightage: 1 },
      { id: '2', checklistType: 'DAILY', periodProgress: { done: 1, due: 2, ratio: 0.5 }, weightage: 1 },
    ],
    expected: 75,
  },
  {
    name: 'one-off completed with binned sibling and repeating sibling',
    items: [
      { id: '1', checklistType: 'INDIVIDUAL', isCompleted: true, weightage: 1 },
      { id: '2', checklistType: 'DAILY', periodProgress: { done: 1, due: 2, ratio: 0.5 }, weightage: 1 },
      { id: '3', checklistType: 'INDIVIDUAL', isCompleted: false, board: 'BIN', weightage: 5 },
    ],
    expected: 75,
  },
  {
    name: 'workflow-COMPLETED one-off without isCompleted flag',
    items: [
      { id: '1', checklistType: 'INDIVIDUAL', workflowStatus: 'COMPLETED', isCompleted: false, weightage: 1 },
    ],
    expected: 100,
  },
  {
    name: 'NOT_REQUIRED one-off without isCompleted flag',
    items: [
      { id: '1', checklistType: 'INDIVIDUAL', workflowStatus: 'NOT_REQUIRED', isCompleted: false, weightage: 1 },
    ],
    expected: 100,
  },
]

